interface TranscriptCue {
  sequence: number;
  startTime: number;
  endTime: number;
  text: string;
  type: 'headline' | 'paragraph';
}

interface TranscriptCueResult {
  cuetext: string;
  cueindex: number;
}

function convertTimestampToSeconds(timestamp: string | number): number {
  if (typeof timestamp === 'number' || !String(timestamp).includes(':')) {
    return Number(timestamp);
  }
  const [minutes, seconds] = timestamp.split(':');
  return parseInt(minutes, 10) * 60 + parseFloat(seconds);
}

interface TranscriptData {
  cues: Array<{
    headline?: { start: number | string; text: string };
    paragraph?: { start: number | string; text: string };
  }>;
}

export const convertTranscriptJson = (data: TranscriptData): TranscriptCue[] => {
  const timeline = data.cues;
  if (!timeline) return [];

  // Sort timeline by start time
  const sortedTimeline = [...timeline].sort((a, b) => {
    const typeA = a.paragraph ? 'paragraph' : 'headline';
    const typeB = b.paragraph ? 'paragraph' : 'headline';
    const startA = a[typeA]!.start;
    const startB = b[typeB]!.start;

    if (startA < startB) return -1;
    if (startA > startB) return 1;
    if (typeA === 'headline' && typeB === 'paragraph') return -1;
    if (typeA === 'paragraph' && typeB === 'headline') return 1;
    return 0;
  });

  return sortedTimeline.map((item, index, array) => {
    const type = item.paragraph ? 'paragraph' : 'headline';
    const { start, text } = item[type]!;
    let endTime: string | number | null = null;

    if (index < array.length - 1) {
      const nextItem = array[index + 1];
      const nextType = nextItem.paragraph ? 'paragraph' : 'headline';
      endTime = nextItem[nextType]!.start;
    }

    let adjustedStart = start;
    if (index > 0) {
      const prevItem = array[index - 1];
      const prevType = prevItem.paragraph ? 'paragraph' : 'headline';
      const prevStart = prevItem[prevType]!.start;
      if (start === prevStart) {
        adjustedStart = Number(start) + 0.1;
      }
    }

    return {
      sequence: index,
      startTime: convertTimestampToSeconds(adjustedStart) * 1000,
      endTime: endTime ? convertTimestampToSeconds(endTime) * 1000 : 0,
      // Digests occasionally omit text; keep a string so layout never reads `.length` on undefined.
      text: text ?? '',
      type,
    } as TranscriptCue;
  });
};

export const findCueByTextAndIndex = (cueArray: TranscriptCue[], timestamp: number): TranscriptCueResult => {
  let start = 0;
  let end = cueArray.length - 1;

  // Find the last cue that starts before or at the timestamp
  while (start <= end) {
    const middle = Math.floor((start + end) / 2);
    const currentCue = cueArray[middle];

    if (currentCue.startTime <= timestamp) {
      start = middle + 1;
    } else {
      end = middle - 1;
    }
  }

  // end will be the index of the last cue that starts before or at the timestamp
  if (end < 0) {
    return { cuetext: '', cueindex: -1 };
  }

  const foundCue = cueArray[end];
  const nextCue = end < cueArray.length - 1 ? cueArray[end + 1] : null;

  // Check if timestamp is within current cue's range
  const isWithinCueRange = nextCue ? timestamp < nextCue.startTime : true;

  return {
    cuetext: isWithinCueRange ? foundCue.text : '',
    cueindex: isWithinCueRange ? foundCue.sequence : -1,
  };
};

export const formatTimestamp = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Estimate a transcript row height for RecyclerListView layout.
 *
 * Guards missing cues / missing `text` (malformed digest or layout races
 * during size changes) — calling `.length` on undefined crashed students
 * opening video transcripts (FLARE-77).
 */
export const estimateCueRowHeight = (
  item: Pick<TranscriptCue, 'text'> | null | undefined,
  width: number,
  fallbackHeight = 48,
): number => {
  const text = item?.text;
  if (typeof text !== 'string' || text.length === 0) {
    return fallbackHeight;
  }
  const charsPerLine = width < 600 ? 70 : 110;
  return Math.ceil(text.length / charsPerLine) * 20;
};
